import { Link, useNavigate } from 'react-router-dom';
import { CalendarDays, ChevronDown, LogOut, ShieldCheck } from 'lucide-react';
import { useAuth, useCurrentUser } from '@/auth/AuthProvider';
import { ORGANIZATION_LABELS } from '@/auth/types';
import { displayYearLabel, useFiscalYear } from '@/fiscal-year/FiscalYearProvider';
import { useConfirm } from '@/components/Confirm';
import { useToast } from '@/components/Toast';
import { errorMessage } from '@/lib/api';

export function BrandMark({ size = 30 }: { size?: number }) {
  return (
    <svg width={size} height={size} viewBox="0 0 32 32" aria-hidden className="bs-brandmark">
      <circle cx="16" cy="16" r="14.5" fill="none" stroke="currentColor" strokeWidth="1" opacity="0.35" />
      <circle cx="16" cy="16" r="9.5" fill="none" stroke="currentColor" strokeWidth="1" opacity="0.6" />
      <circle cx="16" cy="16" r="4.5" fill="none" stroke="currentColor" strokeWidth="1.2" />
      <path d="M16 1.5V30.5M1.5 16H30.5" stroke="currentColor" strokeWidth="0.6" opacity="0.35" />
      <path className="bs-brandmark__needle" d="M16 16 L26.5 8" stroke="var(--signal-500)" strokeWidth="1.8" strokeLinecap="round" />
      <circle cx="16" cy="16" r="1.8" fill="var(--signal-500)" />
    </svg>
  );
}

export function AppHeader() {
  const user = useCurrentUser();
  const { logout } = useAuth();
  const { year, years, switchYear, yearPath } = useFiscalYear();
  const confirm = useConfirm();
  const toast = useToast();
  const navigate = useNavigate();

  const onLogout = async () => {
    const ok = await confirm({ title: 'ログアウトしますか？', message: '入力途中のデータは保存されません。', confirmLabel: 'ログアウト' });
    if (!ok) return;
    try {
      await logout();
      navigate('/login', { replace: true });
    } catch (e) {
      toast.error('ログアウトに失敗しました', errorMessage(e));
    }
  };

  const orgLabel = user.organizationName || ORGANIZATION_LABELS[user.organizationType];

  return (
    <header className="bs-header">
      <Link to={yearPath()} className="bs-header__brand" aria-label="ポータルへ">
        <BrandMark />
        <span className="bs-header__titles">
          <span className="bs-header__system">外国漁船操業等調査・監視事業システム</span>
          <span className="bs-header__org">沖縄県漁業振興基金</span>
        </span>
      </Link>

      <div className="bs-header__right">
        <label className="bs-yearpick">
          <CalendarDays size={16} aria-hidden className="bs-yearpick__icon" />
          <span className="bs-yearpick__caption">事業年度</span>
          <select
            aria-label="事業年度を切り替え"
            className="bs-yearpick__select"
            value={year}
            onChange={(e) => switchYear(Number(e.target.value))}
          >
            {years.map((fy) => (
              <option key={fy.year} value={fy.year}>
                {displayYearLabel(fy)}（{fy.year}）{fy.isCurrent ? ' ・当年度' : ''}
              </option>
            ))}
          </select>
          <ChevronDown size={15} aria-hidden className="bs-yearpick__chevron" />
        </label>

        <div className="bs-header__user">
          <span className="bs-header__avatar" aria-hidden>
            {user.name.slice(0, 1)}
          </span>
          <span className="bs-header__user-text">
            <span className="bs-header__user-name">{user.name}</span>
            <span className="bs-header__user-org">
              <span className={`bs-role bs-role--${roleClass(user.organizationType)}`}>{user.organizationType}</span>
              {orgLabel !== user.organizationType && orgLabel}
            </span>
          </span>
        </div>

        <Link to={yearPath('account/security')} className="bs-header__logout" title="2段階認証の設定">
          <ShieldCheck size={16} aria-hidden />
          2段階認証
        </Link>

        <button type="button" className="bs-header__logout" onClick={onLogout}>
          <LogOut size={16} aria-hidden />
          ログアウト
        </button>
      </div>
    </header>
  );
}

export function roleClass(org: string): string {
  switch (org) {
    case '基金':
      return 'fund';
    case '漁協':
      return 'coop';
    case '水産庁':
      return 'agency';
    default:
      return 'pref';
  }
}
